import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type { ApiResponse, UserDetailDTO } from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import type {
  DeleteCurrentUserPayload,
  UpdateCurrentUserPayload,
} from "@membentry/shared-api";
import { CURRENT_USER_QUERY_KEY } from "../auth/useAuthMutations";

export function useUpdateCurrentUserMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<UserDetailDTO>,
      Error,
      UpdateCurrentUserPayload
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCurrentUserPayload) => {
      const { updateCurrentUser } = await import("@membentry/shared-api");
      return updateCurrentUser(client, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
    ...options,
  });
}

export function useUpdateCurrentUserAvatarMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<UserDetailDTO>, Error, string>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (avatarUrl: string) => {
      const { updateCurrentUserAvatar } = await import("@membentry/shared-api");
      return updateCurrentUserAvatar(client, avatarUrl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
    ...options,
  });
}

export function useDeleteCurrentUserMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<string>,
      Error,
      DeleteCurrentUserPayload | undefined
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload?: DeleteCurrentUserPayload) => {
      const { deleteCurrentUser } = await import("@membentry/shared-api");
      return deleteCurrentUser(client, payload);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
    ...options,
  });
}
